/**
 * Slash-command expansion, shared by the web + React Native composers.
 *
 * A handful of "type a command, message appears with a substitution" affordances
 * (Discord-style) without a bot framework. The expansion is sender-side only —
 * the result is plain text/markdown sent to the server — so this module owns the
 * substitution logic; each platform supplies its own localized popup labels.
 *
 *   /shrug [text]    — append ¯\_(ツ)_/¯
 *   /me <action>     — italic "*name action*" (IRC tradition)
 *   /tableflip       — (╯°□°）╯︵ ┻━┻
 *   /unflip          — ┬─┬ ノ( ゜-゜ノ)
 *   /spoiler <text>  — wrap in ||spoilers||
 *   /lenny [text]    — append ( ͡° ͜ʖ ͡°)
 *   /help            — consume-only; the caller surfaces inline help
 */

const SHRUG = '¯\\_(ツ)_/¯';
const TABLEFLIP = '(╯°□°）╯︵ ┻━┻';
const UNFLIP = '┬─┬ ノ( ゜-゜ノ)';
const LENNY = '( ͡° ͜ʖ ͡°)';

export interface SlashCommandDef {
  /** Bare command without the leading slash. */
  cmd: string;
  /** Whether the command takes a body argument (drives popup hint + trailing space). */
  hasArg: boolean;
}

/** Catalogue order = popup order (most-used first). */
export const SLASH_COMMAND_DEFS: SlashCommandDef[] = [
  { cmd: 'shrug', hasArg: true },
  { cmd: 'me', hasArg: true },
  { cmd: 'tableflip', hasArg: false },
  { cmd: 'unflip', hasArg: false },
  { cmd: 'spoiler', hasArg: true },
  { cmd: 'lenny', hasArg: true },
  { cmd: 'help', hasArg: false },
];

/**
 * Filter + rank the catalogue for the composer's slash popup. Empty query → the
 * full catalogue in declared order; otherwise substring match with
 * command-prefix matches first, then alphabetical.
 */
export function rankSlashCommands(query: string): SlashCommandDef[] {
  const q = query.toLowerCase();
  if (!q) return SLASH_COMMAND_DEFS;
  return SLASH_COMMAND_DEFS.filter((c) => c.cmd.toLowerCase().includes(q)).sort((a, b) => {
    const ap = a.cmd.toLowerCase().startsWith(q) ? 0 : 1;
    const bp = b.cmd.toLowerCase().startsWith(q) ? 0 : 1;
    if (ap !== bp) return ap - bp;
    return a.cmd.localeCompare(b.cmd);
  });
}

export interface ExpandedSlash {
  /** Final content to send (empty when consume-only). */
  expanded: string;
  /** True when nothing should be sent (e.g. /help). */
  consumeOnly: boolean;
  /** True for /help so the caller can surface its localized help text. */
  isHelp: boolean;
}

/**
 * Expand a leading slash command, or null when the input isn't a known command
 * (so the caller just sends the raw text). `someoneFallback` is the localized
 * "someone" used for /me when the display name is unknown.
 */
export function expandSlashCommand(
  raw: string,
  ctx: { displayName?: string | null; someoneFallback?: string },
): ExpandedSlash | null {
  const text = raw.trimStart();
  if (!text.startsWith('/')) return null;
  const match = text.match(/^\/(\w+)\s*([\s\S]*)$/);
  if (!match) return null;
  const cmd = (match[1] ?? '').toLowerCase();
  const body = (match[2] ?? '').trim();

  switch (cmd) {
    case 'shrug':
      return { expanded: body ? `${body} ${SHRUG}` : SHRUG, consumeOnly: false, isHelp: false };
    case 'tableflip':
      return { expanded: body ? `${body} ${TABLEFLIP}` : TABLEFLIP, consumeOnly: false, isHelp: false };
    case 'unflip':
      return { expanded: body ? `${body} ${UNFLIP}` : UNFLIP, consumeOnly: false, isHelp: false };
    case 'lenny':
      return { expanded: body ? `${body} ${LENNY}` : LENNY, consumeOnly: false, isHelp: false };
    case 'me': {
      if (!body) return null; // treat as plain text — server would reject anyway
      const name = ctx.displayName?.trim() || ctx.someoneFallback || 'someone';
      return { expanded: `*${name} ${body}*`, consumeOnly: false, isHelp: false };
    }
    case 'spoiler':
      if (!body) return null;
      return { expanded: `||${body}||`, consumeOnly: false, isHelp: false };
    case 'help':
      return { expanded: '', consumeOnly: true, isHelp: true };
    default:
      return null;
  }
}
